import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HeaderBar } from "@/components/grc/HeaderBar";
import { hasRole, useRole } from "@/context/RoleContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, AlertTriangle, Archive, CheckCircle2, ChevronRight, Circle, Copy,
  Plus, Save, Send, ShieldCheck, Sparkles, Trash2, Undo2,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CitationChip, CompletenessRing, DPIA_STATUSES, ERASURE_TRIGGERS,
  FOURTH_SCHEDULE_EXEMPTIONS, LAWFUL_BASES, LEGITIMATE_USE_GROUNDS,
  PRINCIPAL_CATEGORIES, PURPOSE_CATEGORIES, SECURITY_MEASURES, SENSITIVITIES,
  STATUSES, StatusPill, THIRD_SCHEDULE_CLASSES, VERIFIABLE_CONSENT_METHODS,
  VOLUME_BANDS, WorkspaceNav,
} from "@/components/ropa/shared";
import { fmtDate, fmtDateTime, daysRemaining } from "@/components/grc/shared";
import { RoPAConflictPanel } from "@/components/ropa/RoPAConflictPanel";
import { RiskBadge } from "@/components/vendors/VendorBadges";

const VENDOR_STATUS_BADGE = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ONBOARDING: "bg-blue-50 text-[#008080] border-blue-200",
  SUSPENDED: "bg-amber-50 text-amber-800 border-amber-200",
  OFFBOARDED: "bg-slate-200 text-slate-700 border-slate-300",
};

const SECTIONS = [
  { id: "s1", title: "Identity & Ownership" },
  { id: "s2", title: "Purpose & Data", citation: "Rule 3" },
  { id: "s3", title: "Lawful Basis", citation: "S.4 / 6 / 7" },
  { id: "s4", title: "Systems & Third Parties" },
  { id: "s5", title: "Retention & Erasure", citation: "Rule 8" },
  { id: "s6", title: "Security Measures", citation: "Rule 6" },
  { id: "s7", title: "Cross-Border", citation: "Rule 15" },
  { id: "s8", title: "Children's Data", citation: "S.9" },
  { id: "s9", title: "DPIA & Grievance" },
];

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const Picker = ({ value, onChange, options, placeholder, testId, disabled }) => (
  <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const MultiCheck = ({ options, values, onChange, testIdPrefix, disabled }) => (
  <div className="flex flex-wrap gap-3">
    {options.map((o) => (
      <label key={o} className="flex items-center gap-2 text-xs text-slate-600">
        <Checkbox
          data-testid={`${testIdPrefix}-${o}`}
          disabled={disabled}
          checked={(values || []).includes(o)}
          onCheckedChange={(v) =>
            onChange(v ? [...(values || []), o] : (values || []).filter((x) => x !== o))
          }
        />
        {o}
      </label>
    ))}
  </div>
);

export default function RoPARecordDetail() {
  const { id, recordId } = useParams();
  const navigate = useNavigate();
  const role = useRole();
  const canEdit = hasRole(role, "control_owner");
  const canReview = hasRole(role, "compliance_officer");

  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(null);
  const [missing, setMissing] = useState([]);
  const [history, setHistory] = useState([]);
  const [acting, setActing] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, reason: "" });
  const [linked, setLinked] = useState({ controls: [], findings: [], evidence: [] });
  const [openSections, setOpenSections] = useState(["s1", "s2", "s3"]);
  const [assets, setAssets] = useState([]);
  const [parties, setParties] = useState([]);
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ropa/activities/${recordId}`);
      setRecord(data);
      setMissing(data.missing_fields || []);
      setForm({ ...data, cross_border: data.cross_border || { enabled: false, recipient_countries: [], mechanism: null, sdf_localisation_restricted: false } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load record");
    }
  }, [recordId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/ropa/activities/${recordId}/history`);
      setHistory(data.history || []);
    } catch (e) {
      console.error("Could not load RoPA activity history", e);
    }
  }, [recordId]);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);
  useEffect(() => {
    Promise.all([
      api.get(`/grc/${id}/controls`, { params: { pillar: "P1" } }).catch(() => ({ data: {} })),
      api.get(`/ropa/${id}/conflicts`, { params: { record_id: recordId } }).catch(() => ({ data: {} })),
      api.get(`/grc/${id}/evidence`, { params: { linked_ropa_id: recordId } }).catch(() => ({ data: {} })),
    ]).then(([c, f, e]) => setLinked({
      controls: c.data?.controls || [],
      findings: f.data?.conflicts || [],
      evidence: e.data?.evidence || [],
    }));
  }, [id, recordId]);

  useEffect(() => {
    api.get(`/ropa/${id}/assets`).then(({ data }) => setAssets(data.assets || [])).catch(() => {});
    api.get(`/ropa/${id}/parties`).then(({ data }) => setParties(data.parties || [])).catch(() => {});
    api.get("/grc/teams").then(({ data }) => setTeams(data.teams || [])).catch(() => {});
  }, [id]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));  const setCb = (key, value) => setForm((f) => ({ ...f, cross_border: { ...f.cross_border, [key]: value } }));

  const jumpToSection = (section) => {
    setOpenSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    setTimeout(() => {
      document
        .querySelector(`[data-testid="ropa-section-${section}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, status: form.status, business_function: form.business_function,
        owner_team_id: form.owner_team_id, owner_name: form.owner_name, owner_email: form.owner_email,
        specified_purpose: form.specified_purpose, purpose_category: form.purpose_category,
        data_principal_categories: form.data_principal_categories || [],
        data_elements: (form.data_elements || []).filter((d) => d.element?.trim()),
        data_volume_band: form.data_volume_band,
        lawful_basis: form.lawful_basis, legitimate_use_ground: form.legitimate_use_ground,
        consent_touchpoints: form.consent_touchpoints || [], consent_notice_ref: form.consent_notice_ref,
        withdrawal_mechanism: form.withdrawal_mechanism,
        asset_ids: form.asset_ids || [], party_ids: form.party_ids || [],
        retention_period_months: form.retention_period_months, retention_basis: form.retention_basis,
        erasure_trigger: form.erasure_trigger, log_retention_months: form.log_retention_months,
        third_schedule_class: form.third_schedule_class || "NotApplicable",
        principal_pre_erasure_notice: !!form.principal_pre_erasure_notice,
        security_measures: form.security_measures || [],
        cross_border: form.cross_border,
        involves_children: !!form.involves_children,
        verifiable_consent_method: form.verifiable_consent_method,
        fourth_schedule_exemption: form.fourth_schedule_exemption || "None",
        dpia_required: !!form.dpia_required, dpia_status: form.dpia_status || "NotRequired",
        grievance_route: form.grievance_route, next_review_date: form.next_review_date,
        tags: form.tags || [],
      };
      const { data } = await api.put(`/ropa/activities/${recordId}`, payload);
      setRecord({ ...record, ...data });
      setMissing(data.missing_fields || []);
      setForm((f) => ({ ...f, completeness_pct: data.completeness_pct, updated_at: data.updated_at }));
      toast.success(`Saved — completeness ${data.completeness_pct}%`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg || "Validation failed" : detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const pendingAi = form?.ai_suggested_fields || [];

  const confirmAi = async (fields) => {
    try {
      const { data } = await api.post(`/ropa/activities/${recordId}/confirm-ai`, { fields });
      setForm((f) => ({ ...f, ai_suggested_fields: data.ai_suggested_fields }));
      toast.success(
        data.ai_suggested_fields.length === 0
          ? "All AI fields confirmed — the record can now be submitted"
          : `Confirmed ${fields ? fields.length : "all"} field(s)`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm the field");
    }
  };

  const runAction = async (action, body) => {
    setActing(action);
    try {
      const { data } = await api.post(`/ropa/activities/${recordId}/${action}`, body || {});
      if (action === "clone") {
        toast.success(`Cloned as ${data.record_ref}`);
        navigate(`/assessment/${id}/ropa/${data.id}`);
        return;
      }
      setRecord((r) => ({ ...r, ...data }));
      setForm((f) => ({ ...f, ...data }));
      setMissing(data.missing_fields || []);
      loadHistory();
      toast.success(`Record is now ${data.status}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg || "Action failed" : detail || "Action failed");
    } finally {
      setActing(null);
    }
  };

  const rejectRecord = async () => {
    if (!rejectDialog.reason.trim()) { toast.error("A rejection reason is required"); return; }
    await runAction("reject", { reason: rejectDialog.reason.trim() });
    setRejectDialog({ open: false, reason: "" });
  };

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50">
        <HeaderBar assessmentId={id} />
        <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
          <Skeleton className="h-96 rounded-xl" data-testid="ropa-detail-loading" />
        </main>
      </div>
    );
  }

  const ro = !canEdit;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ropa-record-detail-page">
      <HeaderBar assessmentId={id} />
      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-6">
        <WorkspaceNav assessmentId={id} />

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="ghost" size="sm" data-testid="ropa-back-to-register"
            onClick={() => navigate(`/assessment/${id}/ropa`)}
          >
            <ArrowLeft size={15} className="mr-1.5" /> Register
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-slate-400" data-testid="ropa-detail-ref">{form.record_ref}</span>
            <h1 className="text-lg font-semibold text-[#003366] truncate" data-testid="ropa-detail-name">{form.name}</h1>
            <StatusPill status={form.status} />
          </div>
          {canEdit && (
            <Button
              onClick={save} disabled={saving} data-testid="ropa-save-button"
              className="ml-auto bg-[#008080] hover:bg-[#1a51db]"
            >
              <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save record"}
            </Button>
          )}
        </div>

        {form.rejection_reason && form.status === "Draft" && (
          <div
            className="rounded-xl border border-[#F57C00]/40 bg-[#F57C00]/10 px-4 py-3 flex items-start gap-2.5"
            data-testid="ropa-rejection-banner"
          >
            <AlertTriangle size={16} className="text-[#F57C00] mt-0.5 shrink-0" />
            <div className="text-xs text-[#8A4B00]">
              <span className="font-semibold">Returned for rework</span>
              {form.rejected_by ? ` by ${form.rejected_by}` : ""}
              {form.rejected_at ? ` on ${fmtDateTime(form.rejected_at)}` : ""}: {form.rejection_reason}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          {/* ---------------- Left: accordion form ---------------- */}
          <div className="xl:col-span-8 min-w-0 bg-white border border-slate-200 rounded-xl p-2">
            <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
              {SECTIONS.map((s) => (
                <AccordionItem key={s.id} value={s.id} className="border-slate-100">
                  <AccordionTrigger data-testid={`ropa-section-${s.id}`} className="px-4 hover:no-underline">
                    <span className="text-sm font-semibold text-[#003366] text-left">
                      {s.title}
                      {s.citation && <CitationChip text={s.citation} />}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-6">
                    {s.id === "s1" && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Activity name">
                          <Input data-testid="ropa-field-name" disabled={ro} value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
                        </Field>
                        <Field label="Status">
                          <Picker testId="ropa-field-status" disabled={ro} value={form.status} options={STATUSES} onChange={(v) => set("status", v)} />
                        </Field>
                        <Field label="Business function">
                          <Input data-testid="ropa-field-business-function" disabled={ro} value={form.business_function || ""} onChange={(e) => set("business_function", e.target.value)} />
                        </Field>
                        <Field label="Owner team">
                          <Select value={form.owner_team_id || ""} onValueChange={(v) => set("owner_team_id", v)} disabled={ro}>
                            <SelectTrigger data-testid="ropa-field-owner-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                            <SelectContent>
                              {teams.map((t) => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.name || t.team_name || t.team_id}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Owner name">
                          <Input data-testid="ropa-field-owner-name" disabled={ro} value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} />
                        </Field>
                        <Field label="Owner email">
                          <Input data-testid="ropa-field-owner-email" disabled={ro} value={form.owner_email || ""} onChange={(e) => set("owner_email", e.target.value)} />
                        </Field>
                        <Field label="Next review date">
                          <Input type="date" data-testid="ropa-field-next-review" disabled={ro} value={(form.next_review_date || "").slice(0, 10)} onChange={(e) => set("next_review_date", e.target.value || null)} />
                        </Field>
                        <Field label="Tags (comma separated)">
                          <Input
                            data-testid="ropa-field-tags" disabled={ro}
                            value={(form.tags || []).join(", ")}
                            onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                          />
                        </Field>
                      </div>
                    )}

                    {s.id === "s2" && (
                      <div className="space-y-4">
                        <Field label="Specified purpose">
                          <Textarea rows={3} data-testid="ropa-field-specified-purpose" disabled={ro} value={form.specified_purpose || ""} onChange={(e) => set("specified_purpose", e.target.value)} />
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field label="Purpose category">
                            <Picker testId="ropa-field-purpose-category" disabled={ro} value={form.purpose_category} options={PURPOSE_CATEGORIES} onChange={(v) => set("purpose_category", v)} />
                          </Field>
                          <Field label="Data volume band">
                            <Picker testId="ropa-field-volume-band" disabled={ro} value={form.data_volume_band} options={VOLUME_BANDS} onChange={(v) => set("data_volume_band", v)} />
                          </Field>
                        </div>
                        <Field label="Data principal categories">
                          <MultiCheck
                            options={PRINCIPAL_CATEGORIES} values={form.data_principal_categories} disabled={ro}
                            testIdPrefix="ropa-field-principal" onChange={(v) => set("data_principal_categories", v)}
                          />
                        </Field>
                        <Field label="Data elements">
                          <div className="space-y-2" data-testid="ropa-data-elements">
                            {(form.data_elements || []).map((d, i) => (
                              <div key={i} className="flex gap-2 items-center">
                                <Input
                                  className="flex-1" disabled={ro} data-testid={`ropa-data-element-${i}`}
                                  value={d.element || ""} placeholder="e.g. PAN number"
                                  onChange={(e) => {
                                    const next = [...form.data_elements];
                                    next[i] = { ...next[i], element: e.target.value };
                                    set("data_elements", next);
                                  }}
                                />
                                <div className="w-[130px]">
                                  <Picker
                                    testId={`ropa-data-sensitivity-${i}`} disabled={ro}
                                    value={d.sensitivity || "Low"} options={SENSITIVITIES}
                                    onChange={(v) => {
                                      const next = [...form.data_elements];
                                      next[i] = { ...next[i], sensitivity: v };
                                      set("data_elements", next);
                                    }}
                                  />
                                </div>
                                {!ro && (
                                  <Button
                                    variant="ghost" size="icon" data-testid={`ropa-remove-element-${i}`}
                                    onClick={() => set("data_elements", form.data_elements.filter((_, x) => x !== i))}
                                  >
                                    <Trash2 size={14} className="text-slate-400" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            {!ro && (
                              <Button
                                variant="outline" size="sm" data-testid="ropa-add-data-element"
                                onClick={() => set("data_elements", [...(form.data_elements || []), { element: "", sensitivity: "Low" }])}
                              >
                                <Plus size={14} className="mr-1.5" /> Add element
                              </Button>
                            )}
                          </div>
                        </Field>
                      </div>
                    )}

                    {s.id === "s3" && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Lawful basis" hint="DPDP recognises only Consent or Certain Legitimate Uses.">
                          <Picker testId="ropa-field-lawful-basis" disabled={ro} value={form.lawful_basis} options={LAWFUL_BASES} onChange={(v) => set("lawful_basis", v)} />
                        </Field>
                        {form.lawful_basis === "CertainLegitimateUse" && (
                          <Field label="Legitimate use ground">
                            <Picker testId="ropa-field-legitimate-ground" disabled={ro} value={form.legitimate_use_ground} options={LEGITIMATE_USE_GROUNDS} onChange={(v) => set("legitimate_use_ground", v)} />
                          </Field>
                        )}
                        {form.lawful_basis === "Consent" && (
                          <>
                            <Field label="Consent touchpoints (comma separated)">
                              <Input
                                data-testid="ropa-field-consent-touchpoints" disabled={ro}
                                value={(form.consent_touchpoints || []).join(", ")}
                                onChange={(e) => set("consent_touchpoints", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                              />
                            </Field>
                            <Field label="Withdrawal mechanism">
                              <Input data-testid="ropa-field-withdrawal" disabled={ro} value={form.withdrawal_mechanism || ""} onChange={(e) => set("withdrawal_mechanism", e.target.value)} />
                            </Field>
                            <Field label="Consent notice reference">
                              <Input data-testid="ropa-field-notice-ref" disabled={ro} value={form.consent_notice_ref || ""} onChange={(e) => set("consent_notice_ref", e.target.value)} />
                            </Field>
                          </>
                        )}
                      </div>
                    )}

                    {s.id === "s4" && (
                      <div className="grid sm:grid-cols-2 gap-6">
                        <Field label="Linked assets / systems">
                          <MultiCheck
                            options={assets.map((a) => a.name)} disabled={ro}
                            values={assets.filter((a) => (form.asset_ids || []).includes(a.id)).map((a) => a.name)}
                            testIdPrefix="ropa-field-asset"
                            onChange={(names) => set("asset_ids", assets.filter((a) => names.includes(a.name)).map((a) => a.id))}
                          />
                          {assets.length === 0 && <p className="text-xs text-slate-400">No assets registered yet — add them from the Assets tab.</p>}
                        </Field>
                        <Field label="Processors / recipients">
                          <MultiCheck
                            options={parties.map((p) => p.name)} disabled={ro}
                            values={parties.filter((p) => (form.party_ids || []).includes(p.id)).map((p) => p.name)}
                            testIdPrefix="ropa-field-party"
                            onChange={(names) => set("party_ids", parties.filter((p) => names.includes(p.name)).map((p) => p.id))}
                          />
                          {parties.length === 0 && <p className="text-xs text-slate-400">No parties registered yet — add them from the Parties tab.</p>}
                        </Field>
                      </div>
                    )}

                    {s.id === "s5" && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Retention period (months)">
                          <Input
                            type="number" min={0} data-testid="ropa-field-retention-months" disabled={ro}
                            value={form.retention_period_months ?? ""}
                            onChange={(e) => set("retention_period_months", e.target.value === "" ? null : Number(e.target.value))}
                          />
                        </Field>
                        <Field label="Log retention (months)" hint="Minimum 12 months — DPDP Rule 8(3).">
                          <Input
                            type="number" min={12} data-testid="ropa-field-log-retention" disabled={ro}
                            value={form.log_retention_months ?? ""}
                            onChange={(e) => set("log_retention_months", e.target.value === "" ? null : Number(e.target.value))}
                          />
                        </Field>
                        <Field label="Retention basis">
                          <Input data-testid="ropa-field-retention-basis" disabled={ro} value={form.retention_basis || ""} onChange={(e) => set("retention_basis", e.target.value)} />
                        </Field>
                        <Field label="Erasure trigger">
                          <Picker testId="ropa-field-erasure-trigger" disabled={ro} value={form.erasure_trigger} options={ERASURE_TRIGGERS} onChange={(v) => set("erasure_trigger", v)} />
                        </Field>
                        <Field label="Third Schedule class">
                          <Picker testId="ropa-field-third-schedule" disabled={ro} value={form.third_schedule_class} options={THIRD_SCHEDULE_CLASSES} onChange={(v) => set("third_schedule_class", v)} />
                        </Field>
                        <div className="flex items-center gap-3 pt-6">
                          <Switch
                            data-testid="ropa-field-pre-erasure-notice" disabled={ro}
                            checked={!!form.principal_pre_erasure_notice}
                            onCheckedChange={(v) => set("principal_pre_erasure_notice", v)}
                          />
                          <span className="text-xs text-slate-600">Pre-erasure notice sent to data principal</span>
                        </div>
                      </div>
                    )}

                    {s.id === "s6" && (
                      <Field label="Security measures in place">
                        <MultiCheck
                          options={SECURITY_MEASURES} values={form.security_measures} disabled={ro}
                          testIdPrefix="ropa-field-security" onChange={(v) => set("security_measures", v)}
                        />
                      </Field>
                    )}

                    {s.id === "s7" && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Switch
                            data-testid="ropa-field-cross-border-enabled" disabled={ro}
                            checked={!!form.cross_border?.enabled}
                            onCheckedChange={(v) => setCb("enabled", v)}
                          />
                          <span className="text-xs text-slate-600">Personal data leaves India for this activity</span>
                        </div>
                        {form.cross_border?.enabled && (
                          <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Recipient countries (comma separated)">
                              <Input
                                data-testid="ropa-field-recipient-countries" disabled={ro}
                                value={(form.cross_border?.recipient_countries || []).join(", ")}
                                onChange={(e) => setCb("recipient_countries", e.target.value.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean))}
                              />
                            </Field>
                            <Field label="Transfer mechanism">
                              <Input data-testid="ropa-field-cb-mechanism" disabled={ro} value={form.cross_border?.mechanism || ""} onChange={(e) => setCb("mechanism", e.target.value)} />
                            </Field>
                            <div className="flex items-center gap-3">
                              <Switch
                                data-testid="ropa-field-sdf-restricted" disabled={ro}
                                checked={!!form.cross_border?.sdf_localisation_restricted}
                                onCheckedChange={(v) => setCb("sdf_localisation_restricted", v)}
                              />
                              <span className="text-xs text-slate-600">Restricted by SDF localisation directive</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {s.id === "s8" && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Switch
                            data-testid="ropa-field-involves-children" disabled={ro}
                            checked={!!form.involves_children}
                            onCheckedChange={(v) => set("involves_children", v)}
                          />
                          <span className="text-xs text-slate-600">Processes children's or disabled persons' data</span>
                        </div>
                        {form.involves_children && (
                          <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Verifiable consent method">
                              <Picker testId="ropa-field-vcm" disabled={ro} value={form.verifiable_consent_method} options={VERIFIABLE_CONSENT_METHODS} onChange={(v) => set("verifiable_consent_method", v)} />
                            </Field>
                            <Field label="Fourth Schedule exemption">
                              <Picker testId="ropa-field-fourth-schedule" disabled={ro} value={form.fourth_schedule_exemption} options={FOURTH_SCHEDULE_EXEMPTIONS} onChange={(v) => set("fourth_schedule_exemption", v)} />
                            </Field>
                          </div>
                        )}
                      </div>
                    )}

                    {s.id === "s9" && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <Switch
                            data-testid="ropa-field-dpia-required" disabled={ro}
                            checked={!!form.dpia_required}
                            onCheckedChange={(v) => set("dpia_required", v)}
                          />
                          <span className="text-xs text-slate-600">DPIA required for this activity</span>
                        </div>
                        {form.dpia_required && (
                          <Field label="DPIA status">
                            <Picker testId="ropa-field-dpia-status" disabled={ro} value={form.dpia_status} options={DPIA_STATUSES} onChange={(v) => set("dpia_status", v)} />
                          </Field>
                        )}
                        <Field label="Grievance route">
                          <Input data-testid="ropa-field-grievance" disabled={ro} value={form.grievance_route || ""} onChange={(e) => set("grievance_route", e.target.value)} />
                        </Field>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* ---------------- Right: sticky rail ---------------- */}
          <aside className="xl:col-span-4 min-w-0 xl:sticky xl:top-20 space-y-4" data-testid="ropa-detail-rail">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="ropa-lifecycle-panel">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Lifecycle</div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <StatusPill status={form.status} />
                {form.next_review_date && (
                  <span data-testid="ropa-review-countdown">
                    {(() => {
                      const d = daysRemaining(form.next_review_date);
                      if (d == null) return null;
                      return d < 0
                        ? `Review overdue by ${Math.abs(d)} day(s)`
                        : `Review due in ${d} day(s) · ${fmtDate(form.next_review_date)}`;
                    })()}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {canEdit && form.status === "Draft" && (
                  <Button
                    size="sm" onClick={() => runAction("submit")}
                    title={pendingAi.length > 0
                      ? `Confirm ${pendingAi.length} AI-suggested field(s) before submitting`
                      : "Send this record for compliance review"}
                    disabled={acting === "submit" || pendingAi.length > 0}
                    data-testid="ropa-submit-button" className="bg-[#008080] hover:bg-[#1a51db]"
                  >
                    <Send size={14} className="mr-1.5" /> Submit for review
                  </Button>
                )}
                {canReview && (form.status === "InReview" || form.status === "Stale") && (
                  <Button
                    size="sm" onClick={() => runAction("approve")} disabled={acting === "approve"}
                    data-testid="ropa-approve-button" className="bg-[#388E3C] hover:bg-[#2f7a33]"
                  >
                    <ShieldCheck size={14} className="mr-1.5" /> Approve
                  </Button>
                )}
                {canReview && form.status === "InReview" && (
                  <Button
                    size="sm" variant="outline" data-testid="ropa-reject-button"
                    onClick={() => setRejectDialog({ open: true, reason: "" })}
                    className="border-[#D32F2F]/40 text-[#D32F2F] hover:bg-[#D32F2F]/10"
                  >
                    <Undo2 size={14} className="mr-1.5" /> Reject
                  </Button>
                )}
                {canReview && form.status !== "Retired" && (
                  <Button
                    size="sm" variant="outline" onClick={() => runAction("retire")}
                    disabled={acting === "retire"} data-testid="ropa-retire-button"
                  >
                    <Archive size={14} className="mr-1.5" /> Retire
                  </Button>
                )}
                {canReview && (
                  <Button
                    size="sm" variant="outline" onClick={() => runAction("clone")}
                    disabled={acting === "clone"} data-testid="ropa-clone-button"
                  >
                    <Copy size={14} className="mr-1.5" /> Clone
                  </Button>
                )}
              </div>
              {canEdit && form.status === "Draft" && form.completeness_pct < 60 && (
                <p className="text-[11px] text-[#F57C00]" data-testid="ropa-submit-hint">
                  At least 60% completeness is needed before this record can be submitted.
                </p>
              )}
            </div>

            {pendingAi.length > 0 && (
              <div className="bg-white border border-[#008080]/30 rounded-xl p-5" data-testid="ropa-ai-panel">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] uppercase tracking-wide text-[#008080] font-semibold flex items-center gap-1.5">
                    <Sparkles size={13} /> AI suggestions ({pendingAi.length})
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => confirmAi(null)} data-testid="ropa-ai-confirm-all">
                    Confirm all
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Review each drafted value in the form, then confirm it. Submission stays blocked until none remain.
                </p>
                <ul className="mt-3 space-y-1">
                  {pendingAi.map((field) => (
                    <li key={field} className="flex items-center gap-2 text-xs">
                      <Sparkles size={12} className="text-[#008080] shrink-0" />
                      <span className="flex-1 text-slate-600 truncate">{field.replace(/_/g, " ")}</span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-[#008080]"
                        onClick={() => confirmAi([field])} data-testid={`ropa-ai-confirm-${field}`}>
                        Confirm
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center">
              <CompletenessRing value={form.completeness_pct} />
              <p className="text-[11px] text-slate-400 text-center mt-3">
                Recomputed server-side on every save across all applicable DPDP fields.
              </p>
              <div className="w-full mt-5 pt-5 border-t border-slate-100" data-testid="ropa-missing-checklist">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-3">
                  What's missing {missing.length > 0 && `(${missing.length})`}
                </div>
                {missing.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs text-[#388E3C]" data-testid="ropa-missing-none">
                    <CheckCircle2 size={15} />
                    Every applicable DPDP field is filled.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {missing.map((m) => (
                      <li key={m.field}>
                        <button
                          type="button"
                          data-testid={`ropa-missing-${m.field}`}
                          onClick={() => jumpToSection(m.section)}
                          className="w-full flex items-center gap-2 text-left text-xs text-slate-600 px-2 py-1.5 rounded-lg hover:bg-slate-50 hover:text-[#008080] transition-colors group"
                        >
                          <Circle size={13} className="text-[#F57C00] shrink-0" />
                          <span className="flex-1">{m.label}</span>
                          <ChevronRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <RoPAConflictPanel assessmentId={id} recordId={recordId} compact />

            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ropa-linked-vendors">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">
                Linked vendors {record?.vendors?.length > 0 && `(${record.vendors.length})`}
              </div>
              {(record?.vendors || []).length === 0 ? (
                <p className="text-xs text-slate-400">No vendors linked yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {record.vendors.map((vd) => (
                    <li key={vd.id} className="flex items-center justify-between gap-2" data-testid={`ropa-linked-vendor-${vd.id}`}>
                      <Link
                        to={`/vendors/${vd.id}`}
                        className="flex-1 min-w-0 truncate text-xs text-[#008080] hover:underline"
                        data-testid={`ropa-linked-vendor-link-${vd.id}`}
                      >
                        {vd.name}{vd.processing_position ? ` · ${vd.processing_position}` : ""}
                      </Link>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {vd.cross_border && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200"
                            data-testid={`ropa-vendor-cross-border-${vd.id}`}
                          >
                            Cross-border
                          </span>
                        )}
                        {vd.risk_level && <RiskBadge level={vd.risk_level} testId={`ropa-vendor-risk-${vd.id}`} />}
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border ${VENDOR_STATUS_BADGE[vd.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}
                          data-testid={`ropa-vendor-status-${vd.id}`}
                        >
                          {vd.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {[
              ["Linked P1 controls", linked.controls.map((c) => c.question_id || c.control_id), "ropa-linked-controls"],
              ["Open findings", linked.findings.map((f) => `${f.severity} · ${f.dpdp_ref}`), "ropa-linked-findings"],
              ["Evidence", linked.evidence.map((e) => e.filename), "ropa-linked-evidence"],
            ].map(([title, list, testId]) => (
              <div key={testId} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{title}</div>
                {(list || []).length === 0 ? (
                  <p className="text-xs text-slate-400 mt-2">None linked yet.</p>
                ) : (
                  <ul className="mt-2 space-y-1 text-xs text-slate-600 max-h-40 overflow-y-auto">
                    {list.slice(0, 12).map((x, i) => <li key={`${x}-${i}`} className="truncate">{x}</li>)}
                  </ul>
                )}
              </div>
            ))}

            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ropa-version-history">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                Version history
              </div>
              {history.length === 0 ? (
                <p className="text-xs text-slate-400 mt-2">No changes recorded yet.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {history.slice(0, 12).map((h, i) => (
                    <li key={`${h.timestamp}-${i}`} className="text-xs" data-testid={`ropa-history-item-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#008080] shrink-0" />
                        <span className="font-medium text-slate-700">
                          {h.action.replace("ROPA_", "").replace("_", " ").toLowerCase()}
                        </span>
                        {h.from_status && h.to_status && (
                          <span className="text-slate-400 font-mono text-[10px]">
                            {h.from_status} → {h.to_status}
                          </span>
                        )}
                      </div>
                      <div className="pl-3.5 text-slate-400">
                        {h.user_id || "system"} · {fmtDateTime(h.timestamp)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-1.5 text-xs text-slate-500" data-testid="ropa-detail-metadata">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Record metadata</div>
              <div>Source: <span className="text-slate-700">{form.source}</span></div>
              <div>Version: <span className="text-slate-700">{form.version}</span></div>
              <div>Created by: <span className="text-slate-700">{form.created_by || "—"}</span></div>
              <div>Created: <span className="text-slate-700">{fmtDateTime(form.created_at)}</span></div>
              <div>Updated: <span className="text-slate-700">{fmtDateTime(form.updated_at)}</span></div>
            </div>
          </aside>
        </div>

        <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog((r) => ({ ...r, open: o }))}>
          <DialogContent data-testid="ropa-reject-dialog">
            <DialogHeader><DialogTitle>Return this record for rework</DialogTitle>
              <DialogDescription>The reason is stored on the record and shown to its owner as a banner.</DialogDescription></DialogHeader>
            <Label className="text-xs">Reason (shown to the record owner)</Label>
            <Textarea
              rows={3} data-testid="ropa-reject-reason"
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog((r) => ({ ...r, reason: e.target.value }))}
              placeholder="Retention basis does not cite a statute"
            />
            <DialogFooter>
              <Button onClick={rejectRecord} data-testid="ropa-reject-submit" className="bg-[#D32F2F] hover:bg-[#b32626]">
                Reject record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
